import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from './Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is already logged in
    try {
      const user = localStorage.getItem('dcTech_user');
      if (user) {
        const userData = JSON.parse(user);
        if (userData.userType === 'admin') {
          setIsLoggedIn(true);
        }
      }
    } catch (e) {
      // Invalid localStorage data, clear it
      localStorage.removeItem('dcTech_user');
      localStorage.removeItem('dcTech_token');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Check if user is admin
      if (data.user?.userType !== 'admin') {
        throw new Error('Access denied. Admin accounts only.');
      }

      // Store admin user data
      localStorage.setItem('dcTech_user', JSON.stringify({
        userType: 'admin',
        email: data.user.email,
        name: data.user.name,
        token: data.token
      }));
      localStorage.setItem('dcTech_token', data.token);

      setIsLoggedIn(true);
      setShowLoginModal(false);
      setLoginData({ email: '', password: '' });
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dcTech_user');
    localStorage.removeItem('dcTech_token');
    setIsLoggedIn(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-emerald-900">
      {/* Admin Login Button - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        {isLoggedIn ? (
          <div className="flex items-center gap-3">
            <span className="text-green-400 text-sm flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Admin
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="px-4 py-2 bg-red-600/80 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowLoginModal(true)}
            data-testid="admin-login-btn"
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg border border-white/20 transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Admin Login
          </button>
        )}
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full border border-gray-700 shadow-2xl">
            <div className="p-6 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">Admin Login</h3>
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    setLoginError('');
                    setLoginData({ email: '', password: '' });
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-400 text-sm mt-1">Access restricted to administrators only</p>
            </div>
            
            <form onSubmit={handleLogin} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="admin-email-input"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  placeholder="admin@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="admin-password-input"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  placeholder="••••••••"
                />
              </div>

              {loginError && (
                <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoggingIn}
                data-testid="admin-login-submit"
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoggingIn ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-16">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6">
            Welcome to
          </h1>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent mb-2">
            D.C. Technology Corp.
          </h1>
          <p className="text-xs md:text-sm text-gray-400 mb-8">
            ECRA/ESA #7018534
          </p>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-12">
            Your trusted partner for electrical services, equipment, and professional solutions. 
            Connecting customers with certified professionals across multiple trades.
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 max-w-7xl mx-auto">
          
          {/* Button 1: Contact for Free Estimate */}
          <Link 
            to="/contact"
            className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl p-8 text-center transition-all duration-300 transform hover:scale-105 shadow-2xl border border-blue-500/30"
          >
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">Contact for</h3>
            <h3 className="text-xl font-semibold mb-4">Free Estimate</h3>
            <p className="text-blue-100 text-sm">Get your project quote today</p>
          </Link>

          {/* Button 2: Electrical Equipment - DISABLED */}
          <div 
            className="group relative bg-gradient-to-r from-purple-600/50 to-purple-700/50 text-white rounded-xl p-8 text-center shadow-2xl border border-purple-500/30 opacity-75 cursor-not-allowed overflow-hidden"
          >
            {/* Coming Soon Banner */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-sm py-2 px-4 text-center transform -rotate-3 translate-y-2 z-10">
              COMING SOON
            </div>
            
            <div className="mb-4 mt-4">
              <svg className="w-12 h-12 mx-auto text-purple-200/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-300">Electrical</h3>
            <h3 className="text-xl font-semibold mb-4 text-gray-300">Equipment</h3>
            <p className="text-purple-100/70 text-sm">Browse professional equipment</p>
          </div>

          {/* Button 3: D.C.T. Marketplace */}
          <Link 
            to="/marketplace"
            className="group relative bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl p-8 text-center transition-all duration-300 transform hover:scale-105 shadow-2xl border border-emerald-500/30"
          >
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m14 0v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5m0 0H4m0 0L4 15" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">D.C.T.</h3>
            <h3 className="text-xl font-semibold mb-4">Marketplace</h3>
            <p className="text-emerald-100 text-sm">Access full marketplace</p>
          </Link>

          {/* Button 4: Jobs */}
          <Link 
            to="/jobs"
            className="group relative bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl p-8 text-center transition-all duration-300 transform hover:scale-105 shadow-2xl border border-indigo-500/30"
          >
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">Jobs</h3>
            <h3 className="text-xl font-semibold mb-4">& Careers</h3>
            <p className="text-indigo-100 text-sm">Explore opportunities</p>
          </Link>

          {/* Button 5: Maintenance Prevention - NEW */}
          <Link 
            to="/maintenance-prevention"
            className="group relative bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-xl p-8 text-center transition-all duration-300 transform hover:scale-105 shadow-2xl border border-orange-500/30"
          >
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-orange-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">Maintenance</h3>
            <h3 className="text-xl font-semibold mb-4">Prevention</h3>
            <p className="text-orange-100 text-sm">Motor life calculator</p>
          </Link>

          {/* Button 6: Anti-Puncture Tires - NEW */}
          <a 
            href="/anti-puncture-tires"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="anti-puncture-tires-btn"
            className="group relative bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white rounded-xl p-8 text-center transition-all duration-300 transform hover:scale-105 shadow-2xl border border-slate-500/30 overflow-hidden"
          >
            {/* Tire Image Background */}
            <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-300">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/ccbf4c2e-446a-4188-98bd-b36430933974/images/bf6fba11c43a6ec3190d189e51a0f2e6c4aa763097af3e52d22e2e3c7c248766.jpeg" 
                alt="Anti-Puncture Tire"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto rounded-full border-4 border-slate-400 flex items-center justify-center bg-slate-600/50">
                  <svg className="w-8 h-8 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    <circle cx="12" cy="12" r="4" strokeWidth={2} />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4m0 12v4m10-10h-4M6 12H2" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Anti-Puncture</h3>
              <h3 className="text-xl font-semibold mb-4">Tires</h3>
              <p className="text-slate-300 text-sm">Drill-proof technology</p>
            </div>
          </a>
        </div>

        {/* Footer Section */}
        <div className="text-center mt-16">
          <p className="text-gray-400 text-lg">
            Professional electrical services and equipment solutions for businesses and homeowners
          </p>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default HomePage;